import type { ReactNode } from 'react';

interface CalloutProps {
  type: 'note' | 'warning' | 'tip' | 'danger';
  title: string;
  children: ReactNode;
}

const STYLES = {
  note: {
    container: 'bg-sky-500/5 border-sky-500/20',
    label: 'text-sky-400',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    role: 'note' as const,
  },
  warning: {
    container: 'bg-amber-500/5 border-amber-500/20',
    label: 'text-amber-400',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    role: 'alert' as const,
  },
  tip: {
    container: 'bg-emerald-500/5 border-emerald-500/20',
    label: 'text-emerald-400',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.674M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    role: 'note' as const,
  },
  danger: {
    container: 'bg-rose-500/5 border-rose-500/20',
    label: 'text-rose-400',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    role: 'alert' as const,
  },
} as const;

export default function Callout({ type, title, children }: CalloutProps) {
  const s = STYLES[type];

  return (
    <aside role={s.role} className={`callout my-8 p-6 rounded-2xl border ${s.container}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg border ${s.container} ${s.label}`}>
          {s.icon}
        </div>
        <strong className={`text-sm uppercase tracking-wide ${s.label}`}>{title}</strong>
      </div>
      <div className="text-slate-300 leading-relaxed text-sm">
        {children}
      </div>
    </aside>
  );
}
